import React, { useState } from "react";
import { estimateImpact, formatInr, IMPACT_SOURCES } from "../impact";

const fmt = (n, d = 0) => n.toLocaleString("en-IN", { maximumFractionDigits: d, minimumFractionDigits: d });

// Estimated CO2 and farmer income for the given planted trees per campaign year.
function ImpactPanel({ treesByYear, speciesMix = null, title = "Our impact on Mother Earth" }) {
  const [showHow, setShowHow] = useState(false);
  const i = estimateImpact(treesByYear || {}, new Date(), speciesMix);
  if (!i.plantedTrees) return null;

  const tiles = [
    { label: "CO₂ absorbed so far", value: `${fmt(i.co2ToDateTonnes)} t`, note: "since planting" },
    { label: "CO₂ per year now", value: `${fmt(i.co2PerYearNowTonnes)} t`, note: "and rising as trees grow" },
    { label: "CO₂ in first 5 years", value: `${fmt(i.co2FiveYearTonnes)} t`, note: "projected" },
    { label: "Farmers' fruit income this year", value: formatInr(i.incomeThisYearInr), note: Object.keys(i.bySpecies).join(" and ") || "fruit" },
    { label: "Farmers' income at full bearing", value: `${formatInr(i.incomeAtMaturityInr)} / yr`, note: "from about year 8" },
  ];

  return (
    <div className="bg-white rounded-xl shadow-lg p-6 max-w-5xl mx-auto">
      <h2 className="text-2xl font-bold text-sritaru-800 text-center mb-1">{title}</h2>
      <p className="text-center text-sm text-gray-500 mb-5">
        {fmt(i.plantedTrees)} trees in the ground
        {i.pendingTrees > 0 && <> · {fmt(i.pendingTrees)} more being planted</>} · estimates
      </p>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {tiles.map((t) => (
          <div key={t.label} className="bg-sritaru-50 border border-sritaru-200 rounded-lg p-3 text-center">
            <p className="text-xl md:text-2xl font-bold text-sritaru-700 tabular-nums">{t.value}</p>
            <p className="text-xs text-gray-700 mt-1">{t.label}</p>
            <p className="text-[10px] text-gray-400">{t.note}</p>
          </div>
        ))}
      </div>
      <button onClick={() => setShowHow(!showHow)} className="mt-4 text-xs text-sritaru-700 underline mx-auto block">
        {showHow ? "Hide" : "How are these estimated?"}
      </button>
      {showHow && (
        <div className="mt-3 text-xs text-gray-600 space-y-1 max-w-3xl mx-auto">
          <p>
            A young tree absorbs about 9 kg of CO₂ in its first year and about 108 kg over its first five years,
            growing to around 60 kg a year as it matures. These rates come from the 2024 project figures
            (45.68 t in year one and 540+ t over five years for 5,000 trees), following the IPCC method of
            counting the carbon stored in the tree's wood (about 47% of its dry weight).
          </p>
          <p>
            A mango tree in full bearing earns a farmer about ₹3,000 a year and a guava tree about ₹2,200.
            Guava starts fruiting in year 2 and mango in year 3 or 4, reaching full yield over the following years.
          </p>
          <p>
            The species mix for 2025 isn't confirmed yet, so those trees are counted as mango. The figures assume
            the trees survive and are cared for; real yields depend on rain, variety and market prices.
          </p>
          <p className="text-gray-400">Sources: {IMPACT_SOURCES.join("; ")}.</p>
        </div>
      )}
    </div>
  );
}

export default ImpactPanel;
